import { useEffect, useRef, useState, useCallback } from 'react';
import { api } from '../services/api';
import type { GeocodeResult } from '../types';
import { trackEvent } from '../services/analytics';

interface LocationSearchProps {
  onSelect: (result: GeocodeResult) => void;
  placeholder?: string;
}

export default function LocationSearch({
  onSelect,
  placeholder = 'Search location, landmark...',
}: LocationSearchProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GeocodeResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  const search = useCallback(async (text: string) => {
    const trimmed = text.trim();
    if (trimmed.length === 0) {
      setResults([]);
      setError(null);
      return;
    }

    if (abortRef.current) {
      abortRef.current.abort();
    }
    abortRef.current = new AbortController();

    setLoading(true);
    setError(null);
    try {
      const data = await api.geocode(trimmed);
      setResults(data);
      setIsOpen(data.length > 0);
      trackEvent('search_performed', { result_count: data.length });
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      console.error('Geocode search failed:', err);
      setError('Search failed. Please try again.');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      search(query);
    }, 300);
    return () => clearTimeout(timer);
  }, [query, search]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (result: GeocodeResult) => {
    setQuery(result.name || result.formattedAddress);
    setIsOpen(false);
    trackEvent('search_result_selected', { result_type: result.types?.[0] });
    onSelect(result);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      search(query);
      setIsOpen(true);
    }
  };

  return (
    <div ref={containerRef} className="relative w-full max-w-xs sm:max-w-sm" data-tour="search">
      <div className="floating-panel flex items-center gap-2 px-3 py-2">
        <svg
          className="w-4 h-4 text-gray-400 flex-shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            if (e.target.value.trim().length > 0) {
              setIsOpen(true);
            }
          }}
          onFocus={() => {
            if (results.length > 0 || query.trim().length > 0) {
              setIsOpen(true);
            }
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="bg-transparent text-sm text-gray-700 placeholder-gray-400 focus:outline-none w-full min-w-0"
          aria-label="Search location"
          aria-autocomplete="list"
          aria-controls="location-search-results"
          aria-expanded={isOpen}
        />
        {loading && (
          <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-parksy-600 flex-shrink-0" />
        )}
      </div>

      {isOpen && (
        <div
          id="location-search-results"
          className="absolute top-full left-0 right-0 mt-2 floating-panel max-h-72 overflow-y-auto py-1 z-40"
          role="listbox"
        >
          {error ? (
            <div className="px-4 py-3 text-sm text-red-600">{error}</div>
          ) : results.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500">
              {query.trim().length > 0 ? 'No results found' : 'Start typing to search'}
            </div>
          ) : (
            results.map((result) => (
              <button
                key={result.placeId}
                onClick={() => handleSelect(result)}
                className="w-full text-left px-4 py-2.5 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
                role="option"
              >
                <div className="text-sm font-medium text-gray-900 truncate">
                  {result.name || result.formattedAddress}
                </div>
                <div className="text-xs text-gray-500 truncate">
                  {result.formattedAddress}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
